import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, ShieldCheck, Loader2, Mail, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const formatError = (detail) => {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const TABS = [
  { key: 'password', label: 'Password', icon: KeyRound },
  { key: 'otp', label: 'Email OTP', icon: Mail },
];

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, requestOtp, verifyLoginOtp, resetPassword } = useAuth();

  const [mode, setMode] = useState('password'); // password | otp | reset
  const [step, setStep] = useState('request');   // for otp/reset: request | verify
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const nextPath = new URLSearchParams(location.search).get('next') || '/team';
  const wasExpired = new URLSearchParams(location.search).get('expired') === '1';

  const reset = (m) => {
    setMode(m); setStep('request'); setError(''); setInfo('');
    setOtp(''); setNewPassword('');
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(nextPath);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleRequestCode = async (e, purpose) => {
    e.preventDefault();
    setLoading(true); setError(''); setInfo('');
    try {
      await requestOtp(email.trim().toLowerCase(), purpose);
      setStep('verify');
      setInfo('If an account exists for that email, a 6-digit code is on its way. It expires in 10 minutes.');
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleVerifyLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await verifyLoginOtp(email.trim().toLowerCase(), otp.trim());
      navigate(nextPath);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await resetPassword(email.trim().toLowerCase(), otp.trim(), newPassword);
      setInfo('Password reset successfully. Please sign in with your new password.');
      setMode('password'); setStep('request'); setPassword(''); setOtp(''); setNewPassword('');
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const inputCls = 'w-full border border-gray-300 focus:border-gray-900 outline-none px-4 py-3 text-sm';
  const labelCls = 'block text-xs font-mono tracking-widest text-gray-600 uppercase mb-2';

  const EmailField = (
    <div>
      <label className={labelCls}>Email</label>
      <input type="email" required autoFocus autoComplete="username" value={email}
        onChange={(e) => setEmail(e.target.value)} className={inputCls}
        placeholder="admin@rstandco.co.in" data-testid="admin-login-email" />
    </div>
  );

  const OtpField = (
    <div>
      <label className={labelCls}>6-digit code</label>
      <input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
        className={`${inputCls} font-mono tracking-[0.5em] text-center text-lg`}
        placeholder="••••••" data-testid="admin-login-otp" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="admin-login-page">
      <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff22_1px,transparent_1px),linear-gradient(to_bottom,#ffffff22_1px,transparent_1px)] bg-[size:48px_48px]" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-yellow/10 blur-[160px] rounded-full" />

      <div className="relative w-full max-w-md">
        <div className="bg-white p-8 md:p-10 border border-white/10 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gray-900 flex items-center justify-center">
              <ShieldCheck className="text-brand-yellow" size={20} />
            </div>
            <div>
              <div className="font-mono text-[10px] tracking-[0.2em] text-gray-500 uppercase">RST &amp; Co.</div>
              <h1 className="font-heading text-xl font-bold text-gray-900">Admin Sign-In</h1>
            </div>
          </div>

          {wasExpired && (
            <div className="mb-6 text-xs bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2" data-testid="expired-banner">
              Your session expired. Please sign in again — you&apos;ll be returned to where you were.
            </div>
          )}

          {mode !== 'reset' && (
            <div className="flex border border-gray-200 mb-6">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = mode === t.key;
                return (
                  <button key={t.key} onClick={() => reset(t.key)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${active ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    data-testid={`login-tab-${t.key}`}>
                    <Icon size={14} /> {t.label}
                  </button>
                );
              })}
            </div>
          )}

          {info && (
            <div className="mb-5 text-xs bg-green-50 border border-green-200 text-green-800 px-3 py-2 flex items-start gap-2" data-testid="login-info">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> <span>{info}</span>
            </div>
          )}

          {/* PASSWORD */}
          {mode === 'password' && (
            <form onSubmit={handlePasswordLogin} className="space-y-5">
              {EmailField}
              <div>
                <label className={labelCls}>Password</label>
                <input type="password" required autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} className={inputCls}
                  placeholder="••••••••" data-testid="admin-login-password" />
              </div>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-black text-white rounded-none py-6 font-semibold" data-testid="admin-login-submit">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Signing in…</> : <><LogIn size={16} className="mr-2" /> Sign In</>}
              </Button>
              <button type="button" onClick={() => reset('reset')} className="text-xs text-gray-500 hover:text-gray-900 transition-colors" data-testid="forgot-password-link">
                Forgot your password?
              </button>
            </form>
          )}

          {/* OTP LOGIN */}
          {mode === 'otp' && step === 'request' && (
            <form onSubmit={(e) => handleRequestCode(e, 'login')} className="space-y-5">
              {EmailField}
              <p className="text-xs text-gray-500">We&apos;ll email you a one-time 6-digit code to sign in — no password needed.</p>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-black text-white rounded-none py-6 font-semibold" data-testid="send-otp-btn">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</> : <><Mail size={16} className="mr-2" /> Send code</>}
              </Button>
            </form>
          )}
          {mode === 'otp' && step === 'verify' && (
            <form onSubmit={handleVerifyLogin} className="space-y-5">
              {OtpField}
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</div>}
              <Button type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-black text-white rounded-none py-6 font-semibold" data-testid="verify-otp-btn">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Verifying…</> : <><LogIn size={16} className="mr-2" /> Verify &amp; sign in</>}
              </Button>
              <button type="button" onClick={() => { setStep('request'); setError(''); }} className="text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1">
                <ArrowLeft size={12} /> Use a different email
              </button>
            </form>
          )}

          {/* PASSWORD RESET */}
          {mode === 'reset' && (
            <>
              <div className="flex items-center gap-2 mb-4">
                <KeyRound size={16} className="text-gray-700" />
                <h2 className="font-heading text-base font-bold text-gray-900">Reset password</h2>
              </div>
              {step === 'request' ? (
                <form onSubmit={(e) => handleRequestCode(e, 'password_reset')} className="space-y-5">
                  {EmailField}
                  <p className="text-xs text-gray-500">Enter your account email and we&apos;ll send a code to reset your password.</p>
                  {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</div>}
                  <Button type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-black text-white rounded-none py-6 font-semibold" data-testid="send-reset-btn">
                    {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Sending…</> : <><Mail size={16} className="mr-2" /> Send reset code</>}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleReset} className="space-y-5">
                  {OtpField}
                  <div>
                    <label className={labelCls}>New password</label>
                    <input type="password" required minLength={8} value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)} className={inputCls}
                      placeholder="At least 8 characters" data-testid="reset-new-password" />
                  </div>
                  {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2" data-testid="admin-login-error">{error}</div>}
                  <Button type="submit" disabled={loading} className="w-full bg-gray-900 hover:bg-black text-white rounded-none py-6 font-semibold" data-testid="reset-password-btn">
                    {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Resetting…</> : <><CheckCircle2 size={16} className="mr-2" /> Reset password</>}
                  </Button>
                </form>
              )}
              <button type="button" onClick={() => reset('password')} className="mt-4 text-xs text-gray-500 hover:text-gray-900 flex items-center gap-1" data-testid="back-to-login">
                <ArrowLeft size={12} /> Back to sign in
              </button>
            </>
          )}

          <div className="text-center pt-6 mt-6 border-t border-gray-100">
            <Link to="/" className="text-xs text-gray-500 hover:text-gray-900 transition-colors">← Back to website</Link>
          </div>
        </div>
        <p className="text-center text-white/40 text-xs mt-4">Authorized personnel only. All admin actions are logged.</p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
